import { useEffect, useState } from "react";
import { api } from "@/lib/kfmApi";
import MarkdownLite from "@/components/MarkdownLite";
import CopyButton from "@/components/CopyButton";
import SermonComposer from "@/components/SermonComposer";
import { buildPrayerText, printSimpleDocument } from "@/lib/studyText";
import { Loader2, HandHeart, ChevronDown, ChevronUp, ScrollText, Printer } from "lucide-react";

function fmt(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

function Entry({ e, sermonFeat }) {
  const [open, setOpen] = useState(false);
  const [sermonOpen, setSermonOpen] = useState(false);
  return (
    <div className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5 space-y-3" data-testid="journal-entry">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-lg text-white">{e.title}</h3>
        <span className="text-xs text-slate-500 shrink-0">{fmt(e.created_at)}</span>
      </div>
      {e.reference && <p className="text-xs uppercase tracking-widest text-[#00E5FF]">{e.reference}</p>}
      <p className="text-[15px] text-slate-400 leading-relaxed italic whitespace-pre-wrap">{e.prayer}</p>
      <div className="flex items-center gap-3 pt-1 flex-wrap">
        <CopyButton text={buildPrayerText(e)} label="Prayer copied" testid="journal-copy-prayer" />
        <button
          data-testid="journal-print-prayer"
          onClick={() => printSimpleDocument({ title: e.title || "A prayer", meta: e.reference || "", body: e.prayer || "", italic: true, cta: { phrase: "Turn Scripture into prayer free with OpenWord AI", medium: "pdf", campaign: "prayer_export" } })}
          title="Print / save as PDF"
          className="p-2 rounded-lg text-slate-500 hover:text-[#00E5FF] hover:bg-[#10222A] transition-colors"
        >
          <Printer className="w-4 h-4" strokeWidth={1.5} />
        </button>
        {e.study && (
          <button
            data-testid="journal-toggle-study"
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1.5 text-sm text-[#00E5FF] hover:text-[#00B8CC] transition-colors"
          >
            <ScrollText className="w-4 h-4" strokeWidth={1.5} />
            {open ? "Hide the study" : "The study it became"}
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
        {sermonFeat?.available && e.prayer && (
          <button
            data-testid="journal-to-sermon"
            onClick={() => setSermonOpen(true)}
            className="flex items-center gap-1.5 text-sm text-[#00E5FF] hover:text-[#00B8CC] transition-colors"
          >
            <ScrollText className="w-4 h-4" strokeWidth={1.5} /> Turn into a sermon
          </button>
        )}
      </div>
      {e.study && open && (
        <div data-testid="journal-study" className="rounded-xl bg-[#07090E] border border-[#1E273D] p-4 text-[15px] text-slate-300">
          <MarkdownLite text={e.study} />
        </div>
      )}
      <SermonComposer
        open={sermonOpen}
        onOpenChange={setSermonOpen}
        endpoint="/sermon/from-prayer"
        body={{ prayer: e.prayer }}
        title="Sermon from this prayer"
        description="Shape this prayer into a preachable outline, grounded in Scripture that speaks to it."
        cost={sermonFeat?.cost}
      />
    </div>
  );
}

export default function PrayerJournal() {
  const [entries, setEntries] = useState(null);
  const [sermonFeat, setSermonFeat] = useState(null);

  useEffect(() => {
    api.get("/me/prayer-journal")
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]));
    api.get("/me/subscription")
      .then(({ data }) => setSermonFeat(data.features?.study_to_sermon || null))
      .catch(() => {});
  }, []);

  return (
    <div className="px-6 py-10 md:max-w-2xl md:mx-auto md:py-14 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-[#00E5FF]">
          <HandHeart className="w-4 h-4" strokeWidth={1.5} /> Prayer journal
        </div>
        <h1 className="text-3xl text-white">Prayers you&rsquo;ve carried</h1>
        <p className="text-sm text-slate-400 leading-relaxed">
          The prayers you&rsquo;ve written and turned into a study, gathered in one quiet place &mdash; and the
          studies they became. These are your own words. OpenWord AI never writes, offers, or leads a prayer;
          it only points you back to the Word.
        </p>
      </header>

      {!entries ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" strokeWidth={1.5} /></div>
      ) : entries.length === 0 ? (
        <div className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-10 text-center space-y-2" data-testid="journal-empty">
          <HandHeart className="w-8 h-8 mx-auto text-slate-500" strokeWidth={1.5} />
          <p className="text-slate-400">No prayers gathered yet.</p>
          <p className="text-sm text-slate-500">When you write a prayer and turn it into a study, it will gather here &mdash; in your own words.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="journal-list">
          {entries.map((e) => <Entry key={e.id} e={e} sermonFeat={sermonFeat} />)}
        </div>
      )}
    </div>
  );
}
